import { Autocomplete, TextField } from '@mui/material';

import { useServerSearch } from '@/tracker/hooks/useServerSearch';

import s from './ChipPicker.module.scss';

export type Picker<T> = {
  /** Варианты по введённому тексту; стабильная функция (action.bound стора) */
  search: (query: string) => Promise<T[]>;
  getId: (option: T) => string;
  getLabel: (option: T) => string;
  getHint?: (option: T) => string;
  placeholder: string;
  ariaLabel: string;
  noOptionsText: string;
  onSelect: (option: T) => void;
};

/** Выбор: открывается сразу, выбор добавляет, Esc и потеря фокуса — отмена */
const ChipPicker = <T,>({ picker, onClose }: { picker: Picker<T>; onClose: () => void }) => {
  const { query, setQuery, options, isLoading } = useServerSearch({ search: picker.search });

  return (
    <Autocomplete
      size="small"
      openOnFocus
      options={options}
      // Фильтрует search
      filterOptions={(all) => all}
      loading={isLoading}
      loadingText="Поиск…"
      noOptionsText={picker.noOptionsText}
      value={null}
      inputValue={query}
      onInputChange={(_, value, reason) => reason === 'input' && setQuery(value)}
      getOptionLabel={picker.getLabel}
      renderOption={({ key: _, ...props }, option) => (
        <li key={picker.getId(option)} {...props}>
          <span className={s.option}>
            {picker.getLabel(option)}
            {picker.getHint && <span className={s.option__hint}>{picker.getHint(option)}</span>}
          </span>
        </li>
      )}
      onChange={(_, option) => {
        onClose();

        if (option) picker.onSelect(option);
      }}
      onBlur={onClose}
      // Esc при открытом списке Autocomplete забирает себе — отменяем сразу
      onClose={(_, reason) => reason === 'escape' && onClose()}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={picker.placeholder}
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': picker.ariaLabel } }}
        />
      )}
    />
  );
};

export { ChipPicker };
